import { Mic, Video } from 'lucide-react'
import { cn } from '@/internationalization/cn'
import { PLATFORM_LABEL } from '@/internationalization/constants'
import type { Platform } from '@/types'

/**
 * Platform mark for Teams / Meet / Zoom.
 *
 * Uses a generic glyph tinted with the platform's own brand colour rather than
 * a copy of their logo — a redrawn third-party logo is both a trademark
 * question and a maintenance burden. Colour plus label is enough to identify
 * the platform, and the label is always present for screen readers.
 */
export function PlatformIcon({
  platform,
  size = 'md',
  className,
}: {
  platform: Platform
  size?: 'sm' | 'md'
  className?: string
}) {
  const tint = {
    teams: 'bg-platform-teams/12 text-platform-teams',
    meet: 'bg-platform-meet/12 text-platform-meet',
    zoom: 'bg-platform-zoom/12 text-platform-zoom',
    device: 'bg-surface-muted text-fg-muted',
  }[platform]
  const Glyph = platform === 'device' ? Mic : Video

  return (
    <span
      title={PLATFORM_LABEL[platform]}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded',
        size === 'sm' ? 'h-5 w-5' : 'h-7 w-7',
        tint,
        className,
      )}
    >
      <Glyph aria-hidden className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} />
      <span className="sr-only">{PLATFORM_LABEL[platform]}</span>
    </span>
  )
}
